/**
 * OperationsCenterPage - Central de Operacoes
 * Visao consolidada de todas as rotas em execucao
 *
 * Funcionalidades:
 * - Mapa com todas as rotas ativas
 * - Lista de rotas com status em tempo real
 * - Painel de alertas
 * - Acoes rapidas (pausar, retomar, ver detalhes)
 * - Filtros por status, motorista, veiculo
 */

import {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Badge,
  TextField,
  InputAdornment,
  Alert,
  Collapse,
  Snackbar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Notifications as AlertIcon,
  NotificationsActive as AlertActiveIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Visibility as ViewIcon,
  LocalShipping as VehicleIcon,
  Person as DriverIcon,
  AccessTime as TimeIcon,
  Straighten as DistanceIcon,
  CheckCircle as CompletedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Route as RouteIcon,
  Map as MapIcon,
  ViewList as ListIcon,
  ExpandLess as CollapseIcon,
  Close as CloseIcon,
  VolumeUp as SoundOnIcon,
  VolumeOff as SoundOffIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';

import maplibregl from 'maplibre-gl';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import MapView from '../map/core/MapView';
import { findFonts } from '../map/core/mapUtil';

import routePlanService, {
  PLAN_STATUS,
  STOP_STATUS,
  EVENT_SEVERITY,
} from '../common/services/routePlanService';

import { formatDistance, formatDuration, polylineToGeoJSON } from '../common/services/routingService';
import { DispatcherChatDrawer } from './components';

// Cores para rotas
const ROUTE_COLORS = [
  '#00d4aa', '#f59e0b', '#8b5cf6', '#ef4444',
  '#10b981', '#3b82f6', '#ec4899', '#6366f1',
];

// ============================================================================
// Sub-componentes
// ============================================================================

/**
 * Card de estatisticas no topo
 */
const StatsCard = ({ icon: Icon, label, value, color, tokens }) => (
  <Card
    sx={{
      flex: 1,
      minWidth: 140,
      backgroundColor: tokens?.surface?.raised,
      border: `1px solid ${tokens?.border?.subtle}`,
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: tokens?.radius?.md,
            backgroundColor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
            {label}
          </Typography>
          <Typography sx={{ color: tokens?.text?.primary, fontSize: '20px', fontWeight: 700 }}>
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Card de rota ativa
 */
const ActiveRouteCard = ({
  route,
  vehicles,
  stops,
  position,
  color,
  tokens,
  onView,
  onPause,
  onResume,
  isSelected,
  onClick,
}) => {
  // Calcular progresso
  const completedStops = stops.filter((s) => s.status === STOP_STATUS.COMPLETED).length;
  const totalStops = stops.length;
  const progress = totalStops > 0 ? (completedStops / totalStops) * 100 : 0;

  // Status do plano
  const isPaused = route.status === PLAN_STATUS.APPROVED; // Pausado volta para approved
  const isExecuting = route.status === PLAN_STATUS.EXECUTING;

  // Calcular ETA baseado no tempo restante
  const estimatedEndTime = useMemo(() => {
    if (!route.totalDuration || completedStops >= totalStops) return null;
    const remainingStops = totalStops - completedStops;
    const avgTimePerStop = route.totalDuration / totalStops;
    const remainingTime = remainingStops * avgTimePerStop;
    return new Date(Date.now() + remainingTime * 1000);
  }, [route.totalDuration, completedStops, totalStops]);

  // Determinar se esta atrasado (comparar ETA planejado vs atual)
  const isLate = useMemo(() => {
    if (!route.scheduledEnd) return false;
    const scheduledEnd = new Date(route.scheduledEnd);
    const now = new Date();
    return now > scheduledEnd && progress < 100;
  }, [route.scheduledEnd, progress]);

  // Ultima atualizacao de posicao
  const lastUpdate = position?.fixTime ? new Date(position.fixTime) : null;
  const minutesSinceUpdate = lastUpdate ? Math.floor((Date.now() - lastUpdate.getTime()) / 60000) : null;

  // Veiculo principal
  const vehicle = vehicles[0];
  const deviceName = vehicle?.device?.name || 'Veiculo';

  return (
    <Card
      onClick={onClick}
      sx={{
        backgroundColor: isSelected ? `${color}15` : tokens?.surface?.raised,
        border: `1px solid ${isSelected ? color : tokens?.border?.subtle}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: color,
          transform: 'translateX(4px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: color,
              }}
            />
            <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
              {route.name || `Rota #${route.id}`}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={isExecuting ? 'Em Execucao' : isPaused ? 'Pausada' : route.status}
            sx={{
              height: 22,
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: isExecuting ? `${tokens?.semantic?.success}20` : `${tokens?.semantic?.warning}20`,
              color: isExecuting ? tokens?.semantic?.success : tokens?.semantic?.warning,
            }}
          />
        </Box>

        {/* Info do veiculo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VehicleIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
            <Typography sx={{ fontSize: '12px', color: tokens?.text?.secondary }}>
              {deviceName}
            </Typography>
          </Box>
          {vehicle?.driver && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DriverIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
              <Typography sx={{ fontSize: '12px', color: tokens?.text?.secondary }}>
                {vehicle.driver.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Barra de progresso */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '11px', color: tokens?.text?.tertiary }}>
              Progresso
            </Typography>
            <Typography sx={{ fontSize: '11px', color: tokens?.text?.secondary, fontWeight: 600 }}>
              {completedStops}/{totalStops} paradas
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: tokens?.surface?.elevated,
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Metricas */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
          {route.totalDistance > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DistanceIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
              <Typography sx={{ fontSize: '12px', color: tokens?.text?.secondary }}>
                {formatDistance(route.totalDistance)}
              </Typography>
            </Box>
          )}
          {route.totalDuration > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
              <Typography sx={{ fontSize: '12px', color: tokens?.text?.secondary }}>
                {formatDuration(route.totalDuration)}
              </Typography>
            </Box>
          )}
          {estimatedEndTime && (
            <Tooltip title="Previsão de conclusão">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: 14, color: isLate ? tokens?.semantic?.warning : tokens?.semantic?.success }} />
                <Typography sx={{ fontSize: '12px', color: isLate ? tokens?.semantic?.warning : tokens?.text?.secondary, fontWeight: 500 }}>
                  ETA {estimatedEndTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Ultima atualizacao de posicao */}
        {minutesSinceUpdate !== null && (
          <Typography sx={{ fontSize: '11px', color: minutesSinceUpdate > 5 ? tokens?.semantic?.warning : tokens?.text?.tertiary, mb: 1 }}>
            Última atualização: {minutesSinceUpdate === 0 ? 'agora' : `${minutesSinceUpdate} min atrás`}
          </Typography>
        )}

        {/* Alerta de atraso */}
        {isLate && (
          <Alert
            severity="warning"
            sx={{
              py: 0,
              px: 1,
              mb: 1,
              fontSize: '11px',
              '& .MuiAlert-icon': { fontSize: 16, py: 0.5 },
            }}
          >
            Atrasado em relacao ao planejado
          </Alert>
        )}

        {/* Acoes */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onView(route.id); }}
            sx={{
              flex: 1,
              fontSize: '11px',
              borderColor: tokens?.border?.subtle,
              color: tokens?.text?.secondary,
              '&:hover': { borderColor: color, color },
            }}
          >
            Detalhes
          </Button>
          {isExecuting ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PauseIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => { e.stopPropagation(); onPause(route.id); }}
              sx={{
                flex: 1,
                fontSize: '11px',
                borderColor: tokens?.semantic?.warning,
                color: tokens?.semantic?.warning,
                '&:hover': { backgroundColor: `${tokens?.semantic?.warning}10` },
              }}
            >
              Pausar
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlayIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => { e.stopPropagation(); onResume(route.id); }}
              sx={{
                flex: 1,
                fontSize: '11px',
                borderColor: tokens?.semantic?.success,
                color: tokens?.semantic?.success,
                '&:hover': { backgroundColor: `${tokens?.semantic?.success}10` },
              }}
            >
              Retomar
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Painel de alertas
 */
const AlertsPanel = ({ alerts, tokens, onAcknowledge }) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case EVENT_SEVERITY.CRITICAL:
        return { icon: ErrorIcon, color: tokens?.semantic?.error, label: 'Critico' };
      case EVENT_SEVERITY.ERROR:
        return { icon: ErrorIcon, color: tokens?.semantic?.error, label: 'Erro' };
      case EVENT_SEVERITY.WARNING:
        return { icon: WarningIcon, color: tokens?.semantic?.warning, label: 'Aviso' };
      default:
        return { icon: AlertIcon, color: tokens?.accent?.primary, label: 'Info' };
    }
  };

  if (alerts.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <AlertIcon sx={{ fontSize: 40, color: tokens?.text?.tertiary, mb: 1 }} />
        <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px' }}>
          Nenhum alerta no momento
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
      {alerts.map((alert) => {
        const config = getSeverityConfig(alert.severity);
        const Icon = config.icon;

        return (
          <Box
            key={`${alert.routeId}-${alert.id}`}
            sx={{
              p: 2,
              borderBottom: `1px solid ${tokens?.border?.subtle}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              '&:hover': { backgroundColor: tokens?.surface?.elevated },
            }}
          >
            <Icon sx={{ color: config.color, fontSize: 20, mt: 0.25 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px', fontWeight: 500 }}>
                {alert.description}
              </Typography>
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px', mt: 0.5 }}>
                {alert.routeName} - {new Date(alert.timestamp).toLocaleTimeString('pt-BR')}
              </Typography>
            </Box>
            {!alert.acknowledged && (
              <IconButton
                size="small"
                onClick={() => onAcknowledge(alert.routeId, alert.id)}
                sx={{ color: tokens?.text?.tertiary }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ============================================================================
// Componente Principal
// ============================================================================

const OperationsCenterPage = () => {
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  // Redux - positions para tracking real-time
  const positions = useSelector((state) => state.session.positions, shallowEqual);

  // Estado
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [routeDetails, setRouteDetails] = useState({}); // { planId: { vehicles, stops } }
  const [alerts, setAlerts] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [drivers, setDrivers] = useState([]);
  const [viewMode, setViewMode] = useState('split'); // 'map', 'list', 'split'
  const [alertsPanelOpen, setAlertsPanelOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [refreshing, setRefreshing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);

  // Carregar motoristas
  useEffectAsync(async () => {
    try {
      const response = await fetch('/api/drivers');
      if (response.ok) {
        setDrivers(await response.json());
      }
    } catch (error) {
      console.warn('Failed to load drivers:', error);
    }
  }, []);

  // Carregar rotas ativas
  const loadActiveRoutes = useCallback(async () => {
    try {
      // Buscar todos os planos e filtrar no frontend
      // (backend ExtendedObjectResource.get() nao suporta filtro por status)
      const allPlans = await routePlanService.getRoutePlans();
      const allActivePlans = allPlans.filter(
        (p) => p.status === PLAN_STATUS.EXECUTING || p.status === PLAN_STATUS.APPROVED
      );
      setRoutes(allActivePlans);

      // Carregar detalhes de cada rota
      const details = {};
      for (const plan of allActivePlans) {
        try {
          const summary = await routePlanService.getRoutePlanSummary(plan.id);
          details[plan.id] = {
            vehicles: summary.vehicles || [],
            stops: summary.stops || [],
          };
        } catch (err) {
          console.warn(`Failed to load details for plan ${plan.id}:`, err);
          details[plan.id] = { vehicles: [], stops: [] };
        }
      }
      setRouteDetails(details);

      // Carregar alertas de cada rota
      const allAlerts = [];
      for (const plan of allActivePlans) {
        try {
          const events = await routePlanService.getRoutePlanEvents(plan.id);
          const unacknowledged = events
            .filter((e) => !e.acknowledged)
            .map((e) => ({ ...e, routeId: plan.id, routeName: plan.name || `Rota #${plan.id}` }));
          allAlerts.push(...unacknowledged);
        } catch {
          // Ignorar erro de eventos
        }
      }
      // Ordenar por timestamp (mais recente primeiro)
      allAlerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setAlerts(allAlerts);
    } catch (error) {
      // Silenciar 401 (sessao expirada) — usuario sera redirecionado ao login
      if (error.message?.includes('Unauthorized')) {
        return;
      }
      console.error('[OperationsCenter] Error loading routes:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar rotas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar dados iniciais
  useEffectAsync(async () => {
    await loadActiveRoutes();
  }, [loadActiveRoutes]);

  // Polling para atualizar dados
  useEffect(() => {
    const interval = setInterval(() => {
      loadActiveRoutes();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [loadActiveRoutes]);

  // Notificação sonora para novos alertas críticos
  useEffect(() => {
    if (!soundEnabled) return;

    const criticalAlerts = alerts.filter(
      (a) => a.severity === EVENT_SEVERITY.CRITICAL || a.severity === EVENT_SEVERITY.ERROR,
    );

    if (criticalAlerts.length > 0) {
      // Tocar som de alerta
      try {
        const audio = new Audio('/sounds/alert.wav');
        audio.volume = 0.5;
        audio.play().catch(() => {}); // Ignorar erros de autoplay
      } catch {
        // Ignorar erro de audio
      }
    }
  }, [alerts, soundEnabled]);

  // Refresh manual
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadActiveRoutes();
    setRefreshing(false);
    setSnackbar({ open: true, message: 'Dados atualizados', severity: 'success' });
  };

  // Filtrar rotas
  const filteredRoutes = useMemo(() => {
    let result = routes;

    // Filtro por status
    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter);
    }

    // Filtro por motorista
    if (driverFilter !== 'all') {
      result = result.filter((r) => {
        const details = routeDetails[r.id];
        return details?.vehicles.some((v) => v.driverId === parseInt(driverFilter, 10));
      });
    }

    // Filtro por texto
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter((r) => {
        const details = routeDetails[r.id];
        const vehicleNames = details?.vehicles.map((v) => v.device?.name || '').join(' ').toLowerCase();
        const driverNames = details?.vehicles.map((v) => v.driver?.name || '').join(' ').toLowerCase();
        return (
          (r.name || '').toLowerCase().includes(search)
          || vehicleNames.includes(search)
          || driverNames.includes(search)
        );
      });
    }

    return result;
  }, [routes, statusFilter, driverFilter, searchText, routeDetails]);

  // Obter lista de motoristas ativos nas rotas
  const activeDrivers = useMemo(() => {
    const driverIds = new Set();
    Object.values(routeDetails).forEach((detail) => {
      detail.vehicles.forEach((v) => {
        if (v.driverId) driverIds.add(v.driverId);
      });
    });
    return drivers.filter((d) => driverIds.has(d.id));
  }, [routeDetails, drivers]);

  // Estatisticas
  const stats = useMemo(() => {
    const executing = routes.filter((r) => r.status === PLAN_STATUS.EXECUTING).length;
    const paused = routes.filter((r) => r.status === PLAN_STATUS.APPROVED).length;

    let totalStops = 0;
    let completedStops = 0;
    Object.values(routeDetails).forEach((detail) => {
      totalStops += detail.stops.length;
      completedStops += detail.stops.filter((s) => s.status === STOP_STATUS.COMPLETED).length;
    });

    return {
      total: routes.length,
      executing,
      paused,
      totalStops,
      completedStops,
      pendingStops: totalStops - completedStops,
    };
  }, [routes, routeDetails]);

  // Handlers
  const handleViewRoute = (routeId) => {
    navigate(`/settings/route-execution/${routeId}`);
  };

  const handlePauseRoute = async (routeId) => {
    try {
      await routePlanService.updateRoutePlanStatus(routeId, PLAN_STATUS.APPROVED);
      setSnackbar({ open: true, message: 'Rota pausada', severity: 'success' });
      await loadActiveRoutes();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao pausar rota', severity: 'error' });
    }
  };

  const handleResumeRoute = async (routeId) => {
    try {
      await routePlanService.updateRoutePlanStatus(routeId, PLAN_STATUS.EXECUTING);
      setSnackbar({ open: true, message: 'Rota retomada', severity: 'success' });
      await loadActiveRoutes();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao retomar rota', severity: 'error' });
    }
  };

  const handleAcknowledgeAlert = async (routeId, alertId) => {
    try {
      await routePlanService.acknowledgeEvent(routeId, alertId);
      setAlerts((prev) => prev.filter((a) => !(a.routeId === routeId && a.id === alertId)));
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  // Gerar GeoJSON para o mapa
  const mapData = useMemo(() => {
    const features = [];

    filteredRoutes.forEach((route, index) => {
      const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
      const details = routeDetails[route.id];

      if (!details) return;

      // Adicionar linha da rota para cada veiculo
      details.vehicles.forEach((vehicle) => {
        if (vehicle.routeGeometry) {
          try {
            const geometry = typeof vehicle.routeGeometry === 'string'
              ? polylineToGeoJSON(vehicle.routeGeometry)
              : vehicle.routeGeometry;

            features.push({
              type: 'Feature',
              properties: {
                type: 'route',
                routeId: route.id,
                color,
              },
              geometry,
            });
          } catch {
            // Ignorar erro de geometria
          }
        }
      });

      // Adicionar paradas
      details.stops.forEach((stop, stopIndex) => {
        features.push({
          type: 'Feature',
          properties: {
            type: 'stop',
            routeId: route.id,
            stopId: stop.id,
            name: stop.name,
            status: stop.status,
            order: stopIndex + 1,
            color,
          },
          geometry: {
            type: 'Point',
            coordinates: [stop.longitude, stop.latitude],
          },
        });
      });
    });

    // Adicionar posicoes dos veiculos
    filteredRoutes.forEach((route, index) => {
      const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
      const details = routeDetails[route.id];

      if (!details) return;

      details.vehicles.forEach((vehicle) => {
        const position = positions[vehicle.deviceId];
        if (position) {
          features.push({
            type: 'Feature',
            properties: {
              type: 'vehicle',
              routeId: route.id,
              deviceId: vehicle.deviceId,
              name: vehicle.device?.name || 'Veiculo',
              color,
              course: position.course,
            },
            geometry: {
              type: 'Point',
              coordinates: [position.longitude, position.latitude],
            },
          });
        }
      });
    });

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [filteredRoutes, routeDetails, positions]);

  // Renderizar loading
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: tokens?.surface?.base,
        }}
      >
        <CircularProgress sx={{ color: tokens?.accent?.primary }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens?.surface?.base,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: tokens?.surface?.raised,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/settings/route-planning')}>
              <BackIcon sx={{ color: tokens?.text?.secondary }} />
            </IconButton>
            <Box>
              <Typography sx={{ color: tokens?.text?.primary, fontSize: '20px', fontWeight: 600 }}>
                Central de Operacoes
              </Typography>
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '13px' }}>
                Monitoramento de rotas em tempo real
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Toggle de som */}
            <Tooltip title={soundEnabled ? 'Som de alertas: Ligado' : 'Som de alertas: Desligado'}>
              <IconButton
                onClick={() => setSoundEnabled(!soundEnabled)}
                sx={{
                  color: soundEnabled ? tokens?.semantic?.success : tokens?.text?.tertiary,
                }}
              >
                {soundEnabled ? <SoundOnIcon /> : <SoundOffIcon />}
              </IconButton>
            </Tooltip>

            {/* Botao de chat */}
            <Tooltip title="Mensagens">
              <IconButton
                onClick={() => setChatDrawerOpen(true)}
                sx={{ color: tokens?.text?.secondary }}
              >
                <ChatIcon />
              </IconButton>
            </Tooltip>

            {/* Botao de alertas */}
            <Tooltip title="Alertas">
              <IconButton
                onClick={() => setAlertsPanelOpen(!alertsPanelOpen)}
                sx={{
                  color: alerts.length > 0 ? tokens?.semantic?.warning : tokens?.text?.tertiary,
                }}
              >
                <Badge badgeContent={alerts.length} color="warning">
                  {alerts.length > 0 ? <AlertActiveIcon /> : <AlertIcon />}
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Refresh */}
            <Tooltip title="Atualizar">
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                <RefreshIcon
                  sx={{
                    color: tokens?.text?.secondary,
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>

            {/* Toggle de visualizacao */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, v) => v && setViewMode(v)}
              size="small"
            >
              <ToggleButton value="list">
                <ListIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="split">
                <RouteIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="map">
                <MapIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <StatsCard
            icon={RouteIcon}
            label="Rotas Ativas"
            value={stats.total}
            color={tokens?.accent?.primary}
            tokens={tokens}
          />
          <StatsCard
            icon={PlayIcon}
            label="Em Execucao"
            value={stats.executing}
            color={tokens?.semantic?.success}
            tokens={tokens}
          />
          <StatsCard
            icon={PauseIcon}
            label="Pausadas"
            value={stats.paused}
            color={tokens?.semantic?.warning}
            tokens={tokens}
          />
          <StatsCard
            icon={CompletedIcon}
            label="Entregas Feitas"
            value={stats.completedStops}
            color={tokens?.semantic?.success}
            tokens={tokens}
          />
          <StatsCard
            icon={PendingIcon}
            label="Entregas Pendentes"
            value={stats.pendingStops}
            color={tokens?.semantic?.warning}
            tokens={tokens}
          />
        </Box>
      </Box>

      {/* Painel de alertas */}
      <Collapse in={alertsPanelOpen}>
        <Box
          sx={{
            backgroundColor: tokens?.surface?.raised,
            borderBottom: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
              Alertas ({alerts.length})
            </Typography>
            <IconButton size="small" onClick={() => setAlertsPanelOpen(false)}>
              <CollapseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <AlertsPanel
            alerts={alerts}
            tokens={tokens}
            onAcknowledge={handleAcknowledgeAlert}
          />
        </Box>
      </Collapse>

      {/* Conteudo principal */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Lista de rotas (sidebar) */}
        {(viewMode === 'list' || viewMode === 'split') && (
          <Box
            sx={{
              width: viewMode === 'list' ? '100%' : 380,
              backgroundColor: tokens?.surface?.base,
              borderRight: viewMode === 'split' ? `1px solid ${tokens?.border?.subtle}` : 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Filtros */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${tokens?.border?.subtle}` }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar rota ou veiculo..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: tokens?.text?.tertiary, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: tokens?.surface?.raised,
                  },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="Todas"
                  size="small"
                  onClick={() => setStatusFilter('all')}
                  sx={{
                    backgroundColor: statusFilter === 'all' ? tokens?.accent?.primary : tokens?.surface?.raised,
                    color: statusFilter === 'all' ? '#fff' : tokens?.text?.secondary,
                  }}
                />
                <Chip
                  label="Em Execucao"
                  size="small"
                  onClick={() => setStatusFilter(PLAN_STATUS.EXECUTING)}
                  sx={{
                    backgroundColor: statusFilter === PLAN_STATUS.EXECUTING ? tokens?.semantic?.success : tokens?.surface?.raised,
                    color: statusFilter === PLAN_STATUS.EXECUTING ? '#fff' : tokens?.text?.secondary,
                  }}
                />
                <Chip
                  label="Pausadas"
                  size="small"
                  onClick={() => setStatusFilter(PLAN_STATUS.APPROVED)}
                  sx={{
                    backgroundColor: statusFilter === PLAN_STATUS.APPROVED ? tokens?.semantic?.warning : tokens?.surface?.raised,
                    color: statusFilter === PLAN_STATUS.APPROVED ? '#fff' : tokens?.text?.secondary,
                  }}
                />
              </Box>

              {/* Filtro por motorista */}
              {activeDrivers.length > 0 && (
                <Autocomplete
                  size="small"
                  options={[{ id: 'all', name: 'Todos os motoristas' }, ...activeDrivers]}
                  getOptionLabel={(option) => option.name || ''}
                  value={driverFilter === 'all'
                    ? { id: 'all', name: 'Todos os motoristas' }
                    : activeDrivers.find((d) => d.id === parseInt(driverFilter, 10)) || null}
                  onChange={(_, value) => setDriverFilter(value?.id?.toString() || 'all')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Filtrar por motorista"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <DriverIcon sx={{ color: tokens?.text?.tertiary, fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    mt: 1.5,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.raised,
                    },
                  }}
                />
              )}
            </Box>

            {/* Lista */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {filteredRoutes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <RouteIcon sx={{ fontSize: 48, color: tokens?.text?.tertiary, mb: 1 }} />
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px' }}>
                    Nenhuma rota ativa no momento
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<RouteIcon />}
                    onClick={() => navigate('/settings/route-planning')}
                    sx={{
                      mt: 2,
                      backgroundColor: tokens?.accent?.primary,
                      '&:hover': { backgroundColor: tokens?.accent?.hover },
                    }}
                  >
                    Criar Nova Rota
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {filteredRoutes.map((route, index) => {
                    const details = routeDetails[route.id] || { vehicles: [], stops: [] };
                    const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
                    const mainVehicle = details.vehicles[0];
                    const position = mainVehicle ? positions[mainVehicle.deviceId] : null;

                    return (
                      <ActiveRouteCard
                        key={route.id}
                        route={route}
                        vehicles={details.vehicles}
                        stops={details.stops}
                        position={position}
                        color={color}
                        tokens={tokens}
                        onView={handleViewRoute}
                        onPause={handlePauseRoute}
                        onResume={handleResumeRoute}
                        isSelected={selectedRouteId === route.id}
                        onClick={() => setSelectedRouteId(route.id === selectedRouteId ? null : route.id)}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Mapa */}
        {(viewMode === 'map' || viewMode === 'split') && (
          <Box sx={{ flex: 1, position: 'relative' }}>
            <MapView>
              <OperationsMapLayers
                data={mapData}
                selectedRouteId={selectedRouteId}
                tokens={tokens}
              />
            </MapView>

            {/* Legenda */}
            {filteredRoutes.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                  p: 1.5,
                  maxWidth: 200,
                }}
              >
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '11px', fontWeight: 600, mb: 1 }}>
                  ROTAS ATIVAS
                </Typography>
                {filteredRoutes.slice(0, 5).map((route, index) => {
                  const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
                  return (
                    <Box
                      key={route.id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />
                      <Typography sx={{ color: tokens?.text?.secondary, fontSize: '11px' }}>
                        {route.name || `Rota #${route.id}`}
                      </Typography>
                    </Box>
                  );
                })}
                {filteredRoutes.length > 5 && (
                  <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px', mt: 0.5 }}>
                    +{filteredRoutes.length - 5} mais...
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Chat Drawer */}
      <DispatcherChatDrawer
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        drivers={drivers}
        routePlans={routes}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * Componente de layers do mapa para a Central de Operacoes
 */
const OperationsMapLayers = ({ data, selectedRouteId }) => {
  const sourceId = 'operations-data';

  useEffect(() => {
    // Importar map dinamicamente para evitar problemas de inicializacao
    const setupLayers = async () => {
      const { map } = await import('../map/core/MapView');

      if (!map) return;

      // Adicionar source se nao existir
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'geojson',
          data,
        });

        // Layer de rotas (linhas)
        map.addLayer({
          id: 'operations-routes',
          type: 'line',
          source: sourceId,
          filter: ['==', ['get', 'type'], 'route'],
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 4,
            'line-opacity': 0.8,
          },
        });

        // Layer de paradas (circulos)
        map.addLayer({
          id: 'operations-stops',
          type: 'circle',
          source: sourceId,
          filter: ['==', ['get', 'type'], 'stop'],
          paint: {
            'circle-radius': 8,
            'circle-color': ['get', 'color'],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          },
        });

        // Layer de labels das paradas
        map.addLayer({
          id: 'operations-stops-labels',
          type: 'symbol',
          source: sourceId,
          filter: ['==', ['get', 'type'], 'stop'],
          layout: {
            'text-field': ['get', 'order'],
            'text-size': 10,
            'text-font': findFonts(map),
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#ffffff',
          },
        });

        // Layer de veiculos (simbolos)
        map.addLayer({
          id: 'operations-vehicles',
          type: 'circle',
          source: sourceId,
          filter: ['==', ['get', 'type'], 'vehicle'],
          paint: {
            'circle-radius': 12,
            'circle-color': ['get', 'color'],
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
          },
        });
      } else {
        // Atualizar dados
        map.getSource(sourceId).setData(data);
      }

      // Fit bounds se houver dados
      if (data.features.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        data.features.forEach((feature) => {
          if (feature.geometry.type === 'Point') {
            const [lng, lat] = feature.geometry.coordinates;
            if (lng && lat && !Number.isNaN(lng) && !Number.isNaN(lat)) {
              bounds.extend([lng, lat]);
            }
          } else if (feature.geometry.type === 'LineString') {
            feature.geometry.coordinates.forEach(([lng, lat]) => {
              if (lng && lat && !Number.isNaN(lng) && !Number.isNaN(lat)) {
                bounds.extend([lng, lat]);
              }
            });
          }
        });

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50, maxZoom: 14 });
        }
      }
    };

    setupLayers();

    return () => {
      // Cleanup sera feito na proxima vez que setupLayers for chamado
      // para evitar problemas de timing
    };
  }, [data, selectedRouteId]);

  return null;
};

export default OperationsCenterPage;
